// API triggered broadcasts shared the same route structure as campaigns.  We
// have defined the setup and overview steps with the `campaigns.campaign`
// setup path below, the `replaceCampaignWithBroadcasts` util function will
// help us replace the `campaigns.campaign` path with `broadcasts.broadcast`

import { ClickSource } from './analytics/transforms/campaign';

function replaceCampaignWithBroadcasts(steps: CampaignOverviewTab[][]): CampaignOverviewTab[][];
function replaceCampaignWithBroadcasts(steps: CampaignSetupStep[]): CampaignSetupStep[];
function replaceCampaignWithBroadcasts(steps: CampaignOverviewTab[][] | CampaignSetupStep[]) {
  return steps.map((stepOrSteps) => {
    if (Array.isArray(stepOrSteps)) {
      return stepOrSteps.map(replaceCampaignWithBroadcastsStep);
    }

    return replaceCampaignWithBroadcastsStep(stepOrSteps);
  });
}

function replaceCampaignWithBroadcastsStep(step: CampaignSetupStep | CampaignOverviewTab) {
  return { ...step, path: step.path.replace('campaigns.campaign', 'broadcasts.broadcast') };
}

export type CampaignSetupStep = {
  name: string;
  path: string;
  title: string;
  validationKey?: string;
  description?: string;
  buttonTitle?: string;
};

export type CampaignOverviewTab = {
  title: string;
  path: string;
  params?: {
    source: ClickSource;
  };
};

export const CAMPAIGN_SETUP_STEPS: Record<string, CampaignSetupStep> = Object.freeze({
  recipients: {
    name: 'recipients',
    path: 'campaigns.campaign.setup.recipients',
    title: 'Recipients',
    validationKey: 'areRecipientsReady',
  },
  settings: {
    name: 'settings',
    path: 'campaigns.campaign.setup.settings',
    title: 'Settings',
    validationKey: 'validWorkflowSettings',
  },
  trigger: {
    name: 'trigger',
    path: 'campaigns.campaign.setup.trigger',
    title: 'Trigger',
    validationKey: 'hasRecipients',
  },
  api_triggered_workflow: {
    name: 'workflow',
    path: 'campaigns.campaign.setup.workflow.actions',
    title: 'Workflow',
    validationKey: 'hasEffectActions',
    description: 'Configure what happens after this broadcast is triggered',
  },
  default_workflow: {
    name: 'workflow',
    path: 'campaigns.campaign.setup.workflow.actions',
    title: 'Workflow',
    validationKey: 'hasEffectActions',
    description: 'Configure what happens after someone matches',
  },
  goal_and_exit: {
    name: 'goal',
    path: 'campaigns.campaign.setup.goal',
    title: 'Goal & Exit',
    buttonTitle: 'Goal',
    validationKey: 'validConversion',
    description: 'Measure campaign performance and track conversions',
  },
  goal: {
    name: 'goal',
    path: 'campaigns.campaign.setup.goal',
    title: 'Goal',
    validationKey: 'validConversion',
    description: 'Measure broadcast performance and track conversions',
  },
  review: {
    name: 'review',
    path: 'campaigns.campaign.setup.review',
    title: 'Review',
  },
});

export const DEFAULT_CAMPAIGN_SETUP_STEPS = [
  CAMPAIGN_SETUP_STEPS.trigger,
  CAMPAIGN_SETUP_STEPS.settings,
  CAMPAIGN_SETUP_STEPS.goal_and_exit,
  CAMPAIGN_SETUP_STEPS.default_workflow,
  CAMPAIGN_SETUP_STEPS.review,
];

export const API_TRIGGERED_BROADCAST_SETUP_STEPS = replaceCampaignWithBroadcasts([
  CAMPAIGN_SETUP_STEPS.recipients,
  CAMPAIGN_SETUP_STEPS.settings,
  CAMPAIGN_SETUP_STEPS.goal,
  CAMPAIGN_SETUP_STEPS.api_triggered_workflow,
  CAMPAIGN_SETUP_STEPS.review,
]);

export const WEBHOOK_CAMPAIGN_SETUP_STEPS = [
  CAMPAIGN_SETUP_STEPS.trigger,
  CAMPAIGN_SETUP_STEPS.default_workflow,
  CAMPAIGN_SETUP_STEPS.review,
];

export const CAMPAIGN_OVERVIEW_TABS: Record<string, CampaignOverviewTab> = {
  overview: {
    title: 'Overview',
    path: 'campaigns.campaign.overview.index',
  },
  triggering_details: {
    title: 'Triggering Details',
    path: 'campaigns.campaign.overview.triggering_details',
  },
  broadcasts: {
    title: 'Broadcasts',
    path: 'campaigns.campaign.overview.broadcasts',
  },
  metrics: {
    title: 'Metrics',
    path: 'campaigns.campaign.overview.metrics',
  },
  journeys: {
    title: 'Journeys',
    path: 'campaigns.campaign.overview.subjects',
  },
  drafts: {
    title: 'Drafts',
    path: 'campaigns.campaign.overview.drafts',
  },
  sent: {
    title: 'Sent',
    path: 'campaigns.campaign.overview.sent',
  },
  recipients: {
    title: 'Recipients',
    path: 'campaigns.campaign.overview.recipients',
  },
  trigger: {
    title: 'Trigger',
    path: 'campaigns.campaign.overview.trigger',
    params: {
      source: ClickSource.Tab,
    },
  },
  workflow: {
    title: 'Workflow',
    path: 'campaigns.campaign.overview.workflow.actions',
    params: {
      source: ClickSource.Tab,
    },
  },
  goal: {
    title: 'Goal',
    path: 'campaigns.campaign.overview.goal',
  },
  goal_and_exit: {
    title: 'Goal & Exit',
    path: 'campaigns.campaign.overview.goal',
    params: {
      source: ClickSource.Tab,
    },
  },
  settings: {
    title: 'Settings',
    path: 'campaigns.campaign.overview.settings',
  },
  tests: {
    title: 'A/B Tests',
    path: 'campaigns.campaign.overview.tests',
    params: {
      source: ClickSource.Tab,
    },
  },
};

export const DEFAULT_CAMPAIGN_OVERVIEW_TAB_GROUPS = [
  [CAMPAIGN_OVERVIEW_TABS.overview, CAMPAIGN_OVERVIEW_TABS.metrics, CAMPAIGN_OVERVIEW_TABS.workflow],
  [
    CAMPAIGN_OVERVIEW_TABS.trigger,
    CAMPAIGN_OVERVIEW_TABS.settings,
    CAMPAIGN_OVERVIEW_TABS.goal_and_exit,
    CAMPAIGN_OVERVIEW_TABS.tests,
  ],
  [CAMPAIGN_OVERVIEW_TABS.drafts, CAMPAIGN_OVERVIEW_TABS.sent, CAMPAIGN_OVERVIEW_TABS.journeys],
];

export const API_TRIGGERED_BROADCAST_OVERVIEW_TAB_GROUPS = replaceCampaignWithBroadcasts([
  [CAMPAIGN_OVERVIEW_TABS.overview, CAMPAIGN_OVERVIEW_TABS.metrics, CAMPAIGN_OVERVIEW_TABS.workflow],
  [
    CAMPAIGN_OVERVIEW_TABS.triggering_details,
    CAMPAIGN_OVERVIEW_TABS.recipients,
    CAMPAIGN_OVERVIEW_TABS.settings,
    CAMPAIGN_OVERVIEW_TABS.goal,
    CAMPAIGN_OVERVIEW_TABS.tests,
  ],
  [CAMPAIGN_OVERVIEW_TABS.drafts, CAMPAIGN_OVERVIEW_TABS.broadcasts, CAMPAIGN_OVERVIEW_TABS.journeys],
]);

export const WEBHOOK_CAMPAIGN_OVERVIEW_TAB_GROUPS = [
  [CAMPAIGN_OVERVIEW_TABS.overview, CAMPAIGN_OVERVIEW_TABS.workflow, CAMPAIGN_OVERVIEW_TABS.trigger],
  [CAMPAIGN_OVERVIEW_TABS.drafts, CAMPAIGN_OVERVIEW_TABS.sent, CAMPAIGN_OVERVIEW_TABS.journeys],
];
